/**
 * Builder Filter (layer on top of a bundle builder)
 *
 * Adds a compact filter bar to a bundle builder and hides product cards that
 * don't carry the chosen tags. Nothing in the app is changed: the app still
 * owns steps, min/max rules, pricing and add-to-cart.
 *
 * Configuration comes from theme settings (Theme settings > Builder Filters)
 * via window.builderFilterConfig.
 *
 * FILTER GROUPS (setting "Filter groups"), one per line:
 *     [set:]key[:hidden]|Label|Option=tag,tag;Option@key=tag
 *   - "set" limits the group to one builder set (e.g. kids, baby). No set =
 *     every builder.
 *   - ":hidden" means the group is applied from the link but not shown as
 *     chips (used for the occasion, which is chosen before the builder).
 *   - Options are separated by ";". Tags after "=" are separated by "," and
 *     any of them counts. "Label@key" sets the key used in links; without it
 *     the key is derived from the label.
 *
 * WHICH SET IS ACTIVE: the link's ?set=<name>, else the first line of the
 * setting "Builder sets" (url-fragment=set) whose fragment is in the URL.
 *
 * LINKS: ?<group key>=<option key> pre-selects, e.g.
 *     ...&set=kids&occasion=birthday&age=2-4
 *   "<group>=all" means "no filter for this group" (Just Because / View All).
 *
 * REQUIRED GROUP: setting "Required before the builder opens" (set:group).
 * If that set is active and the link carries no valid value for the group,
 * the visitor is sent to the "required" URL (the occasion page).
 *
 * STEPS: setting "Filter on steps" (e.g. 2,3) limits the bar and the
 * filtering to the product-picking steps. Blank = every step.
 * A group can name its own steps with a last part "steps=3", e.g.
 *     kids:sibling|Gender|Boy=Gender: Boy;Girl=Gender: Girl|steps=3
 * Step numbers are the builder app's own (its first step is 1).
 *
 * Tags are read from /products/<handle>.js (the cards expose the handle),
 * through one shared queue, cached in sessionStorage. A failed lookup leaves
 * the card visible. If a filter would empty a step, that step shows
 * everything plus a note.
 */

(function () {
  'use strict';

  var cfg = window.builderFilterConfig;
  if (!cfg || !cfg.groupsRaw) return;

  var PROFILES = cfg.profiles || [{
    name: 'bundlesuite',
    root: '.bundlesuite-container',
    card: '.product-card',
    section: '.individual-products-container, .collection-products-container',
    handleAttr: 'data-product-handle',
    insertBefore: '',
    stepItem: ''
  }];
  var ROOT, CARD, SECTION, HANDLE_ATTR, INSERT_BEFORE, PROFILE_NAME, STEP_ITEM, LOAD_MORE;
  var STEPS_CONTAINER, STEP_BACK, STEP_NEXT, STEP_LABEL, STEP_COUNTER, KEEP_ON_OCC;
  var EVENTS = cfg.events || ['bundleInitialized', 'stepperTextChanged', 'byob:cartUpdated'];
  var CACHE_KEY = 'builderFilterTags.v2';
  var CACHE_TTL = 5 * 60 * 1000; // re-read tags after 5 minutes so new tags show up
  var LOAD_ALL = cfg.loadAll !== false;
  var TAG_COLLECTIONS = String(cfg.tagCollections || '').split(/[\s,]+/)
    .map(function (s) { return s.trim(); }).filter(Boolean);
  var CONCURRENCY = 3;   // parallel tag lookups; keep low to stay under the storefront rate limit
  var PAUSE_MS = 120;    // small gap between lookups on the same worker
  var ALL = 'all';
  var ALL_LABEL = cfg.allLabel || 'View All';

  var STEPS = String(cfg.steps || '').split(',')
    .map(function (s) { return parseInt(s, 10); })
    .filter(function (n) { return n > 0; });

  var startParams = new URLSearchParams(window.location.search);
  var activeSet = resolveSet();
  var groups = parseGroups(cfg.groupsRaw).filter(function (g) { return !g.set || g.set === activeSet; });
  if (!groups.length) return;
  var groupByKey = {};
  groups.forEach(function (g) { groupByKey[g.key] = g; });
  var visibleGroups = groups.filter(function (g) { return !g.hidden; });
  var hiddenGroups = groups.filter(function (g) { return g.hidden; });

  // Required group (e.g. kids must have an occasion before building).
  //   mode "step": the choice is Step 1 inside the builder; the app's own
  //                steps are shown as 2..N.
  //   mode "page": visitors without a value are sent to requiredUrl.
  var occ = null;
  if (cfg.required) {
    var rq = String(cfg.required).split(':');
    var rSet = slug(rq[0]), rKey = slug(rq[1] || '');
    var rGroup = groupByKey[rKey];
    if (rKey && rGroup && rSet === activeSet) {
      var rVal = slug(startParams.get(rKey) || '');
      var rValid = rVal === ALL || rGroup.options.some(function (o) { return o.key === rVal; });
      if ((cfg.requiredMode || 'page') === 'step') {
        occ = { key: rKey, group: rGroup, chosen: rValid, isAll: rVal === ALL, active: !rValid, tab: null, panel: null };
        // Tell the theme's step-image script (theme.liquid) to number the app's steps from 2.
        window.builderStepOffset = 1;
      } else if (!rValid && cfg.requiredUrl) {
        window.location.replace(cfg.requiredUrl);
        return;
      }
    }
  }

  var tags = {};      // handle -> [lowercase tags]
  var pending = {};   // handle -> true while queued or fetching
  var failures = {};  // handle -> last error message (for support/debugging)
  var selected = {};  // group key -> option key ('' = all)
  var bar = null;
  var scheduled = false;

  // Read-only debug handle for support
  window.builderFilter = {
    tags: tags, pending: pending, failures: failures, groups: groups, set: activeSet,
    get selected() { return selected; },
    get occasion() { return occ ? { chosen: occ.chosen, isAll: occ.isAll, active: occ.active } : null; }
  };

  // ---------------------------------------------------------------- helpers
  function slug(s) {
    return String(s).toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function resolveSet() {
    var fromLink = slug(startParams.get(cfg.setParam || 'set') || '');
    if (fromLink) return fromLink;
    var here = window.location.pathname + window.location.search;
    var lines = String(cfg.setsRaw || '').split(/\r?\n/);
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i].trim();
      var eq = line.lastIndexOf('=');
      if (eq < 1) continue;
      var frag = line.slice(0, eq).trim();
      var name = slug(line.slice(eq + 1));
      if (frag && name && here.indexOf(frag) !== -1) return name;
    }
    return '';
  }

  function parseGroups(raw) {
    var out = [];
    String(raw).split(/\r?\n/).forEach(function (line) {
      line = line.trim();
      if (!line || line.charAt(0) === '#') return;
      var parts = line.split('|').map(function (p) { return p.trim(); });
      var keyField, label, optsRaw;
      var gSteps = null;
      if (parts.length >= 4 && /^stepss*=/i.test(parts[parts.length - 1])) {
        gSteps = parts.pop().replace(/^stepss*=/i, '').split(',')
          .map(function (n) { return parseInt(n, 10); }).filter(function (n) { return n > 0; });
      }
      if (parts.length >= 3) { keyField = parts[0]; label = parts[1]; optsRaw = parts.slice(2).join('|'); }
      else if (parts.length === 2) { label = parts[0]; keyField = label; optsRaw = parts[1]; }
      else return;

      var kp = keyField.split(':').map(function (p) { return p.trim(); }).filter(Boolean);
      var hidden = false;
      if (kp.length > 1 && kp[kp.length - 1].toLowerCase() === 'hidden') { hidden = true; kp.pop(); }
      var set = kp.length > 1 ? slug(kp[0]) : '';
      var key = slug(kp[kp.length - 1] || label);

      var options = [];
      optsRaw.split(';').forEach(function (o) {
        var eq = o.indexOf('=');
        if (eq === -1) return;
        var left = o.slice(0, eq).trim();
        var at = left.lastIndexOf('@');
        var olabel = at > 0 ? left.slice(0, at).trim() : left;
        var okey = slug(at > 0 ? left.slice(at + 1) : left);
        var otags = o.slice(eq + 1).split(',').map(function (t) { return t.trim().toLowerCase(); }).filter(Boolean);
        if (!olabel || !okey || !otags.length) return;
        options.push({ key: okey, label: olabel, tags: otags });
      });
      if (key && options.length) out.push({ set: set, key: key, label: label, hidden: hidden, options: options, steps: gSteps });
    });
    return out;
  }

  function optionOf(g, key) {
    for (var i = 0; i < g.options.length; i++) if (g.options[i].key === key) return g.options[i];
    return null;
  }

  function readCache() {
    try {
      var c = JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}') || {};
      if (!c.t || Date.now() - c.t >= CACHE_TTL) return {};
      cacheStamp = c.t;
      return c.tags || {};
    } catch (e) { return {}; }
  }
  var cacheStamp = 0;
  function writeCache(obj) {
    if (!cacheStamp) cacheStamp = Date.now(); // age counts from the first fresh read
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: cacheStamp, tags: obj })); } catch (e) { /* ignore */ }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () { scheduled = false; apply(); });
  }

  // ------------------------------------------------------------ tag loading
  // One shared queue with a fixed number of workers, whoever asks. Calling
  // loadTags() repeatedly (every re-apply does) only adds new handles; it
  // never starts extra parallel batches or overwrites the cache.
  var queue = [];
  var running = 0;
  var cacheSeeded = false;

  function fetchOne(h, attempt) {
    return fetch('/products/' + encodeURIComponent(h) + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (r.ok) return r.json();
        throw new Error('status ' + r.status);
      })
      .catch(function (err) {
        if (attempt >= 3) throw err;
        var delay = 600 * attempt * attempt;
        return new Promise(function (res) { setTimeout(res, delay); }).then(function () { return fetchOne(h, attempt + 1); });
      });
  }

  function worker() {
    if (!queue.length) return;
    running++;
    var h = queue.shift();
    fetchOne(h, 1)
      .then(function (p) {
        tags[h] = ((p && p.tags) || []).map(function (t) { return String(t).toLowerCase().trim(); });
        writeCache(tags);
      })
      .catch(function (err) {
        // Unknown stays unknown: the card remains visible rather than being hidden by mistake.
        failures[h] = String(err && err.message || err);
      })
      .then(function () {
        delete pending[h];
        running--;
        schedule();
        setTimeout(worker, PAUSE_MS);
      });
  }

  function loadTags(handles) {
    if (!cacheSeeded) {
      cacheSeeded = true;
      var cached = readCache();
      Object.keys(cached).forEach(function (h) { if (!tags[h]) tags[h] = cached[h]; });
      loadCollections();
    }
    handles.forEach(function (h) {
      if (!h || tags[h] || pending[h] || failures[h]) return;
      pending[h] = true;
      queue.push(h);
    });
    // While the collection reads run, single lookups wait: most will be answered by them.
    if (bulkRunning) return;
    while (running < CONCURRENCY && queue.length) worker();
  }

  // Read whole collections at once (/collections/<handle>/products.json, 250 a
  // page), which answers most cards in a few requests instead of one each.
  var bulkRunning = 0;
  function loadCollections() {
    if (!TAG_COLLECTIONS.length) return;
    if (Object.keys(tags).length && cacheStamp) return; // fresh cache already
    TAG_COLLECTIONS.forEach(function (handle) {
      bulkRunning++;
      readPage(handle, 1);
    });
  }
  function readPage(handle, page) {
    fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250&page=' + page, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('status ' + r.status); return r.json(); })
      .then(function (data) {
        var list = (data && data.products) || [];
        list.forEach(function (p) {
          var t = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',');
          tags[p.handle] = t.map(function (x) { return String(x).toLowerCase().trim(); }).filter(Boolean);
          delete pending[p.handle];
        });
        if (list.length) writeCache(tags);
        if (list.length === 250 && page < 8) return readPage(handle, page + 1);
        bulkDone();
      })
      .catch(function () { bulkDone(); });
  }
  function bulkDone() {
    bulkRunning--;
    if (bulkRunning > 0) return;
    // Look up singly whatever the collections didn't cover.
    queue = queue.filter(function (h) { return !tags[h]; });
    Object.keys(pending).forEach(function (h) { if (tags[h]) delete pending[h]; });
    while (running < CONCURRENCY && queue.length) worker();
    schedule();
  }

  function collectHandles(root) {
    var seen = {};
    var list = [];
    root.querySelectorAll(CARD).forEach(function (card) {
      var h = card.getAttribute(HANDLE_ATTR);
      if (h && !seen[h]) { seen[h] = true; list.push(h); }
    });
    return list;
  }

  // ------------------------------------------------------------------ steps
  function currentStep() {
    if (!STEP_ITEM) return 0;
    var items = document.querySelectorAll(STEP_ITEM);
    for (var i = 0; i < items.length; i++) {
      if (items[i].classList.contains('active') || items[i].getAttribute('aria-current') === 'step') return i + 1;
    }
    return 0;
  }

  // Does group g filter on step n? Its own "steps=" list, else the
  // "Filter on steps" setting, else every step. n = 0 means unknown.
  function groupOn(g, n) {
    var list = g.steps && g.steps.length ? g.steps : STEPS;
    return !list.length || n === 0 || list.indexOf(n) !== -1;
  }

  // ----------------------------------------------------------------- filter
  function activeKeys() {
    return Object.keys(selected).filter(function (k) { return selected[k] && groupByKey[k]; });
  }

  function cardMatches(card, keys) {
    var h = card.getAttribute(HANDLE_ATTR);
    var t = tags[h];
    // Still being checked: keep it out of sight so it doesn't flash in and out.
    // Lookup failed (or no handle): show it rather than hide it by mistake.
    if (!t) return !pending[h];
    return keys.every(function (gk) {
      var opt = optionOf(groupByKey[gk], selected[gk]);
      if (!opt) return true;
      return opt.tags.some(function (x) { return t.indexOf(x) !== -1; });
    });
  }

  function apply() {
    var root = document.querySelector(ROOT);
    if (!root) return;
    ensureBar(root);
    ensureOccasion(root);

    // Steps rendered after start-up (or after the app re-renders) bring new
    // products; pick their tags up here so nothing depends on the observer.
    var fresh = collectHandles(root).filter(function (h) { return !tags[h] && !pending[h]; });
    if (fresh.length) loadTags(fresh);

    var keys = activeKeys();
    var sections = root.querySelectorAll(SECTION);
    var step = currentStep();
    if (step !== lastLoadStep) { lastLoadStep = step; loadMoreClicks = 0; } // each step gets its own Load More budget
    var visibleInView = 0, totalInView = 0, visibleAll = 0, totalAll = 0;
    var matchedInView = 0, checkingAny = false;
    var barOn = !STEPS.length;
    var more = canLoadMore(root);

    sections.forEach(function (sec, idx) {
      var cards = sec.querySelectorAll(CARD);
      var inView = sec.offsetParent !== null;

      // Is this section one of the steps we filter on, and with which groups?
      var stepNo = STEP_ITEM ? step : idx + 1;
      var stepOk = groups.some(function (g) { return groupOn(g, stepNo); });
      var secKeys = keys.filter(function (k) { return groupOn(groupByKey[k], stepNo); });
      if (stepOk && (inView || STEP_ITEM)) barOn = true;

      if (!stepOk) {
        cards.forEach(function (card) { setHidden(card, false); });
        setSectionNote(sec, false);
        return;
      }

      var decisions = [];
      var visible = 0;
      var checking = 0;
      cards.forEach(function (card) {
        var show = !secKeys.length || cardMatches(card, secKeys);
        if (!show && pending[card.getAttribute(HANDLE_ATTR)]) checking++;
        decisions.push([card, show]);
        if (show) visible++;
      });
      if (checking) checkingAny = true;
      if (inView || STEP_ITEM) matchedInView += visible;
      // Only fall back to "show everything" once every product has been
      // checked and the app has nothing more to load for this step.
      var fallback = secKeys.length > 0 && cards.length > 0 && visible === 0 && checking === 0 && !more;
      decisions.forEach(function (d) {
        setHidden(d[0], !(d[1] || fallback));
      });
      setSectionNote(sec, fallback);

      var shown = fallback ? cards.length : visible;
      totalAll += cards.length;
      visibleAll += shown;
      if (inView) { totalInView += cards.length; visibleInView += shown; }
    });

    // If no section registers as on screen (e.g. the app is mid-render), count everything.
    if (totalInView === 0) { totalInView = totalAll; visibleInView = visibleAll; }
    if (occ && occ.active) barOn = false; // Step 1 (occasion) is showing instead of the products
    if (bar) { var disp = barOn ? '' : 'none'; if (bar.style.display !== disp) bar.style.display = disp; }
    // Show only the groups that filter on the current step.
    if (bar && STEP_ITEM) {
      bar.querySelectorAll('.bf-group[data-group]').forEach(function (el) {
        var g = groupByKey[el.getAttribute('data-group')];
        var d = !g || groupOn(g, step) ? '' : 'none';
        if (el.style.display !== d) el.style.display = d;
      });
    }
    if (STEP_ITEM) keys = keys.filter(function (k) { return groupOn(groupByKey[k], step); });
    var filtering = keys.length > 0 && barOn;
    var loadingMore = more && (LOAD_ALL || (filtering && !checkingAny && matchedInView < MIN_VISIBLE));
    var busy = checkingAny || loadingMore || (filtering && !!loadMoreButton(root, true) && !loadMoreButton(root));
    updateStatus(visibleInView, totalInView, keys.length > 0, busy);
    if (loadingMore) loadMore(root);
  }

  // DOM writes are guarded everywhere in apply(): the builder app refreshes
  // itself when its markup changes, and that refresh re-triggers apply().
  // Writing only on real changes keeps the two from chasing each other.
  function setHidden(card, hide) {
    if (card.classList.contains('bf-hidden') !== hide) card.classList.toggle('bf-hidden', hide);
  }

  function setSectionNote(sec, fallback) {
    var note = sec.previousElementSibling;
    var isNote = note && note.classList && note.classList.contains('bf-section-note');
    if (fallback) {
      if (!isNote) {
        note = document.createElement('div');
        note.className = 'bf-section-note';
        sec.parentNode.insertBefore(note, sec);
      }
      var noteText = cfg.emptyText || 'No items in this step match your filters, so everything is shown.';
      if (note.textContent !== noteText) note.textContent = noteText;
    } else if (isNote) {
      note.parentNode.removeChild(note);
    }
  }

  // Some builders page their products. When a filter leaves a step thin, ask
  // the app for more (bounded), so matches further down still show up.
  var loadMoreClicks = 0;
  var MIN_VISIBLE = cfg.minVisible || 12;  // keep loading until this many matches show
  var MAX_LOAD_MORE = cfg.maxLoadMore || 60; // per step; 60 x 9 products is plenty
  var lastLoadStep = -1;

  // The app disables its load-more button while a page is loading, so a
  // visible-but-disabled button means "more is on the way", not "no more".
  function loadMoreButton(root, allowDisabled) {
    if (!LOAD_MORE) return null;
    var btn = root.querySelector(LOAD_MORE) || document.querySelector(LOAD_MORE);
    if (!btn || btn.offsetParent === null) return null;
    if (btn.disabled && !allowDisabled) return null;
    return btn;
  }
  function canLoadMore(root) {
    return loadMoreClicks < MAX_LOAD_MORE && !!loadMoreButton(root, true);
  }
  function loadMore(root) {
    var btn = loadMoreButton(root);
    if (!btn) return;
    loadMoreClicks++;
    btn.click();
  }

  // ------------------------------------------------ occasion as Step 1
  // Our own first step, in front of the app's steps. The app is not changed:
  // we add a tab to its step bar, show a panel in place of its product area,
  // and number its steps from 2. Used when a group is required in "step" mode.
  function buildOccPanel() {
    var p = document.createElement('div');
    p.className = 'bf-occ-panel';
    var opts = occ.group.options;
    var rem = 3 - (opts.length % 3);
    var html = '<h2 class="bf-occ-title">' + escapeHtml(cfg.occasionHeading || 'Choose your occasion') + '</h2>';
    if (cfg.occasionText) html += '<p class="bf-occ-sub">' + escapeHtml(cfg.occasionText) + '</p>';
    html += '<div class="bf-occ-options">';
    var imgs = cfg.occasionImages || {};
    // An occasion with an image (Theme settings > Builder Look) is an image
    // button; the label stays as its accessible name.
    function optionHtml(key, label, extra) {
      var src = imgs[key];
      var inner = src
        ? '<img class="bf-occ-img" src="' + escapeHtml(src) + '" alt="' + escapeHtml(label) + '">'
        : escapeHtml(label);
      return '<button type="button" class="bf-occ-option' + (src ? ' bf-occ-option--img' : '') + (extra || '') +
        '" data-option="' + key + '"' + (src ? ' aria-label="' + escapeHtml(label) + '"' : '') + '>' + inner + '</button>';
    }
    opts.forEach(function (o) { html += optionHtml(o.key, o.label, ''); });
    html += optionHtml(ALL, cfg.occasionAllLabel || 'Just Because (View All)', ' bf-occ-option--all')
      .replace('<button ', '<button style="grid-column:span ' + rem + '" ');
    html += '</div><div class="bf-occ-msg" role="status" aria-live="polite"></div>';
    p.innerHTML = html;
    p.addEventListener('click', function (e) {
      var btn = e.target.closest('.bf-occ-option');
      if (btn) chooseOccasion(btn.getAttribute('data-option'));
    });
    return p;
  }

  // Step 1 tab image: the "current" or "other" picture from Builder Look.
  function occTabImage() {
    var e = (window.builderStepImages || {})[1];
    if (e && typeof e === 'object') return occ && occ.active ? e.on : e.off;
    return e || (cfg.stepImages || {})[1] || '';
  }

  function ensureOccasion(root) {
    if (!occ) return;
    var cont = STEPS_CONTAINER ? document.querySelector(STEPS_CONTAINER) : null;
    if (cont && !(occ.tab && occ.tab.parentNode === cont)) {
      occ.tab = document.createElement('div');
      occ.tab.className = 'bf-occ-tab';
      occ.tab.setAttribute('role', 'button');
      occ.tab.setAttribute('tabindex', '0');
      occ.tab.setAttribute('aria-label', 'Step 1: ' + (cfg.occasionHeading || 'Choose your occasion'));
      var img = occTabImage();
      occ.tab.innerHTML = img
        ? '<img class="bf-occ-tab-img" src="' + escapeHtml(img) + '" alt="" aria-hidden="true">'
        : '<span class="bf-occ-tab-text">Step 1</span>';
      occ.tab.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); showOccasion(true); }
      });
      cont.insertBefore(occ.tab, cont.firstChild);
    }
    if (!(occ.panel && occ.panel.parentNode)) {
      if (!occ.panel) occ.panel = buildOccPanel();
      var keep = KEEP_ON_OCC ? root.querySelector(KEEP_ON_OCC) : null;
      if (keep && keep.parentNode === root) root.insertBefore(occ.panel, keep.nextSibling);
      else root.insertBefore(occ.panel, root.firstChild);
    }
    // Only write when something changed, so observers are not re-triggered.
    if (!root.classList.contains('bf-occ-on')) root.classList.add('bf-occ-on');
    if (root.classList.contains('bf-occ-active') !== occ.active) root.classList.toggle('bf-occ-active', occ.active);
    if (occ.tab) {
      if (occ.tab.classList.contains('active') !== occ.active) occ.tab.classList.toggle('active', occ.active);
      var tabImg = occ.tab.querySelector('img');
      var want = occTabImage();
      if (tabImg && want && tabImg.getAttribute('src') !== want) tabImg.setAttribute('src', want);
      var cur = occ.active ? 'step' : 'false';
      if (occ.tab.getAttribute('aria-current') !== cur) occ.tab.setAttribute('aria-current', cur);
    }
    var chosenKey = occ.chosen ? (occ.isAll ? ALL : selected[occ.key]) : null;
    occ.panel.querySelectorAll('.bf-occ-option').forEach(function (btn) {
      var on = btn.getAttribute('data-option') === chosenKey;
      if (btn.classList.contains('is-active') !== on) btn.classList.toggle('is-active', on);
    });
    relabelSteps();
  }

  // The app labels its steps "Step 1..N" in the summary panel; show them as 2..N+1.
  // On narrow screens it also shows a "Step X/Y" counter by the arrows.
  function relabelSteps() {
    if (STEP_COUNTER) {
      document.querySelectorAll(STEP_COUNTER).forEach(function (el) {
        // Remember the app's own text ("Step 2/5"); it changes when the app
        // moves step. Derive ours from it every time, so switching between
        // our Step 1 and the app's steps always re-numbers.
        var text = el.textContent;
        var orig = el.getAttribute('data-bf-orig');
        if (text !== el.getAttribute('data-bf-written')) { orig = text; el.setAttribute('data-bf-orig', orig); }
        var m = /^(\s*\D*?)(\d+)(\s*\/\s*)(\d+)(\s*)$/.exec(orig || '');
        if (!m) return;
        var cur = occ.active ? 1 : parseInt(m[2], 10) + 1;
        var want = m[1] + cur + m[3] + (parseInt(m[4], 10) + 1) + m[5];
        if (el.getAttribute('data-bf-written') !== want) el.setAttribute('data-bf-written', want);
        if (el.textContent !== want) el.textContent = want;
      });
    }
    if (!STEP_LABEL) return;
    document.querySelectorAll(STEP_LABEL).forEach(function (el) {
      var orig = el.getAttribute('data-bf-step');
      if (orig === null) {
        var m = /^(\D*?)(\d+)\s*$/.exec(el.textContent);
        if (!m) return;
        orig = m[1] + '|' + m[2];
        el.setAttribute('data-bf-step', orig);
      }
      var parts = orig.split('|');
      var want = parts[0] + (parseInt(parts[1], 10) + 1);
      if (el.textContent !== want) el.textContent = want;
    });
  }

  function chooseOccasion(key) {
    occ.chosen = true;
    occ.isAll = key === ALL;
    selected[occ.key] = occ.isAll ? '' : key;
    occ.active = false;
    loadMoreClicks = 0;
    setOccMessage('');
    syncUrl();
    if (bar && bar.parentNode) bar.parentNode.removeChild(bar); // rebuild: the fixed occasion tag changed
    bar = null;
    apply();
    var root = document.querySelector(ROOT);
    if (root && root.getBoundingClientRect().top < 0) root.scrollIntoView({ block: 'start' });
  }

  function showOccasion(on) {
    if (!occ) return;
    occ.active = !!on;
    if (!on) setOccMessage('');
    apply();
    if (on && occ.panel) {
      var r = occ.panel.getBoundingClientRect();
      if (r.top < 0 || r.top > window.innerHeight * 0.8) occ.panel.scrollIntoView({ block: 'center' });
    }
  }

  function setOccMessage(text) {
    if (!occ || !occ.panel) return;
    var el = occ.panel.querySelector('.bf-occ-msg');
    if (el && el.textContent !== text) el.textContent = text;
  }

  // Step tabs and arrows: keep the shopper on Step 1 until an occasion is chosen.
  function onOccClick(e) {
    if (!occ) return;
    var t = e.target;
    if (!t || !t.closest) return;
    if (occ.tab && occ.tab.contains(t)) { showOccasion(true); return; }
    var appTab = STEP_ITEM ? t.closest(STEP_ITEM) : null;
    var next = STEP_NEXT ? t.closest(STEP_NEXT) : null;
    var back = STEP_BACK ? t.closest(STEP_BACK) : null;

    if (occ.active) {
      if (!appTab && !next && !back) return;
      if (back) { e.stopImmediatePropagation(); e.preventDefault(); return; }
      if (!occ.chosen) {
        e.stopImmediatePropagation(); e.preventDefault();
        setOccMessage(cfg.occasionRequiredText || 'Please choose an occasion to continue.');
        return;
      }
      if (next) {
        // Step 1 -> Step 2 is the app's first step
        e.stopImmediatePropagation(); e.preventDefault();
        showOccasion(false);
        var first = STEP_ITEM ? document.querySelector(STEP_ITEM) : null;
        if (first && !first.classList.contains('active')) first.click();
        return;
      }
      showOccasion(false); // app tab: let the app switch to it
      return;
    }
    if (back && currentStep() === 1) {
      // Back from the app's first step returns to Step 1 (occasion)
      e.stopImmediatePropagation(); e.preventDefault();
      showOccasion(true);
    }
  }

  // --------------------------------------------------------------------- UI
  function ensureBar(root) {
    if (bar && bar.parentNode) return;
    bar = document.createElement('div');
    // Phones show only a "Narrow it down" toggle until it's opened (CSS ignores bf-collapsed on wider screens).
    bar.className = 'bf-bar bf-collapsed';
    var html = '';
    html += '<button type="button" class="bf-toggle" aria-expanded="false">' + escapeHtml(cfg.title || 'Filter') +
      '<span class="bf-toggle-count"></span><span class="bf-toggle-icon" aria-hidden="true"></span></button>';
    if (cfg.title) html += '<div class="bf-title">' + escapeHtml(cfg.title) + '</div>';

    // Pre-applied (hidden) groups: shown as a fixed tag, with a way back
    hiddenGroups.forEach(function (g) {
      var opt = optionOf(g, selected[g.key]);
      var isOcc = occ && occ.key === g.key;
      var allText = isOcc ? (cfg.occasionAllShort || 'Just Because') : ALL_LABEL;
      var change = '';
      if (isOcc) change = '<button type="button" class="bf-change" data-bf-change="1">' + escapeHtml(cfg.changeLabel || 'Change') + '</button>';
      else if (cfg.requiredUrl) change = '<a class="bf-change" href="' + escapeHtml(cfg.requiredUrl) + '">' + escapeHtml(cfg.changeLabel || 'Change') + '</a>';
      html += '<div class="bf-group bf-fixed" data-group="' + g.key + '"><span class="bf-label">' + escapeHtml(g.label) + '</span>' +
        '<span class="bf-tag">' + escapeHtml(opt ? opt.label : allText) + '</span>' + change + '</div>';
    });

    visibleGroups.forEach(function (g) {
      html += '<div class="bf-group" data-group="' + g.key + '"><span class="bf-label">' + escapeHtml(g.label) + '</span>';
      html += '<button type="button" class="bf-chip" data-group="' + g.key + '" data-option="">' + escapeHtml(ALL_LABEL) + '</button>';
      g.options.forEach(function (o) {
        html += '<button type="button" class="bf-chip" data-group="' + g.key + '" data-option="' + o.key + '">' + escapeHtml(o.label) + '</button>';
      });
      html += '</div>';
    });
    html += '<div class="bf-foot"><span class="bf-status"></span></div>';
    bar.innerHTML = html;

    var anchor = INSERT_BEFORE ? root.querySelector(INSERT_BEFORE) : null;
    if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(bar, anchor);
    else root.parentNode.insertBefore(bar, root);

    bar.addEventListener('click', function (e) {
      if (e.target.closest('.bf-toggle')) {
        var open = bar.classList.toggle('bf-collapsed') === false;
        bar.querySelector('.bf-toggle').setAttribute('aria-expanded', open ? 'true' : 'false');
        return;
      }
      if (e.target.closest('[data-bf-change]')) { showOccasion(true); return; }
      var chip = e.target.closest('.bf-chip');
      if (!chip) return;
      var g = chip.getAttribute('data-group'), o = chip.getAttribute('data-option');
      selected[g] = selected[g] === o ? '' : o;
      loadMoreClicks = 0;
      syncUrl();
      renderChips();
      apply();
    });
    renderChips();
  }

  function renderChips() {
    if (!bar) return;
    bar.querySelectorAll('.bf-chip').forEach(function (chip) {
      var cur = selected[chip.getAttribute('data-group')] || '';
      var on = cur === chip.getAttribute('data-option');
      chip.classList.toggle('is-active', on);
      chip.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    // On the phone toggle: how many filters are on, e.g. "Narrow it down (2)"
    var n = visibleGroups.filter(function (g) { return selected[g.key]; }).length;
    var countEl = bar.querySelector('.bf-toggle-count');
    var countText = n ? ' (' + n + ')' : '';
    if (countEl && countEl.textContent !== countText) countEl.textContent = countText;
  }

  function updateStatus(visible, total, active, busy) {
    if (!bar) return;
    var el = bar.querySelector('.bf-status');
    var text = '';
    if (active) {
      var loading = busy || Object.keys(pending).length > 0;
      text = loading ? 'Finding matches…' : 'Showing ' + visible + ' of ' + total;
    }
    if (el.textContent !== text) el.textContent = text;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // -------------------------------------------------------------------- URL
  function readUrl() {
    groups.forEach(function (g) {
      var v = slug(startParams.get(g.key) || '');
      if (!v || v === ALL) return;
      if (optionOf(g, v)) selected[g.key] = v;
    });
  }

  function syncUrl() {
    if (!window.history || !window.history.replaceState) return;
    var params = new URLSearchParams(window.location.search);
    visibleGroups.forEach(function (g) {
      if (selected[g.key]) params.set(g.key, selected[g.key]); else params.delete(g.key);
    });
    if (occ && occ.chosen) params.set(occ.key, occ.isAll ? ALL : selected[occ.key]);
    var qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
  }

  // ------------------------------------------------------------------ styles
  function injectStyles() {
    var accent = cfg.accent || '#5b8a8a';
    var css = document.createElement('style');
    css.textContent =
      '.bf-hidden{display:none !important}' +
      // Load all: the app's Load More button stays in the page (we click it) but out of sight.
      (LOAD_ALL && LOAD_MORE ? LOAD_MORE + '{visibility:hidden !important;height:0 !important;min-height:0 !important;margin:0 !important;padding:0 !important;border:0 !important;overflow:hidden !important}' : '') +
      // one compact row that wraps: title | fixed tags | group chips | status
      '.bf-bar{display:flex;flex-wrap:wrap;align-items:center;gap:6px 16px;margin:0 0 14px;padding:8px 12px;border:1px solid #e6e2dc;border-radius:8px;background:#faf8f5;font-size:12px;line-height:1.3;box-sizing:border-box}' +
      '.bf-title{font-weight:600;font-size:13px;color:#222;padding-right:12px;border-right:1px solid #ddd}' +
      '.bf-group{display:flex;flex-wrap:wrap;align-items:center;gap:4px}' +
      '.bf-label{font-weight:600;color:#666;margin-right:4px;text-transform:uppercase;font-size:11px;letter-spacing:.04em}' +
      '.bf-chip{appearance:none;border:1px solid #cfd8d8;background:#fff;color:#333;border-radius:999px;padding:3px 10px;font:inherit;font-size:12px;line-height:1.3;cursor:pointer;transition:background .15s,color .15s,border-color .15s}' +
      '.bf-chip:hover{border-color:' + accent + '}' +
      '.bf-chip.is-active{background:' + accent + ';border-color:' + accent + ';color:#fff}' +
      '.bf-tag{display:inline-block;border-radius:999px;padding:3px 10px;background:#efe9df;color:#333;font-weight:600}' +
      '.bf-change{margin-left:4px;color:#666;text-decoration:underline;font-size:11px}' +
      '.bf-foot{display:flex;align-items:center;gap:10px;margin-left:auto;white-space:nowrap}' +
      '.bf-status{color:#666}' +
      '.bf-section-note{margin:0 0 10px;padding:6px 12px;border-radius:6px;background:#fff6e5;color:#7a5a12;font-size:12px}' +
      '@media (max-width:599px){.bf-title{border:0;padding:0;width:100%}.bf-foot{margin-left:0;width:100%}}' +
      // Phones: a "Narrow it down" toggle; groups show only when it's open
      '.bf-toggle{display:none}' +
      '@media (max-width:599px){' +
        '.bf-toggle{display:flex;align-items:center;width:100%;appearance:none;border:0;background:none;padding:2px 0;font:inherit;font-weight:600;font-size:13px;color:#222;cursor:pointer;text-align:left}' +
        '.bf-toggle-icon{margin-left:auto;width:8px;height:8px;border-right:2px solid #444;border-bottom:2px solid #444;transform:rotate(45deg);transition:transform .2s}' +
        '.bf-bar:not(.bf-collapsed) .bf-toggle-icon{transform:rotate(-135deg)}' +
        '.bf-bar .bf-title{display:none}' +
        '.bf-bar.bf-collapsed > :not(.bf-toggle){display:none !important}' +
        '.bf-bar.bf-collapsed{padding:6px 12px}' +
      '}' +
      'button.bf-change{appearance:none;border:0;background:none;padding:0;font:inherit;cursor:pointer}';

    if (occ) {
      var a2 = cfg.builderAccent || '#5c878d';
      var keep = KEEP_ON_OCC ? ':not(' + KEEP_ON_OCC + ')' : '';
      css.textContent +=
        // while Step 1 shows, hide the app's product area (kept in the DOM so the app keeps its state)
        '.bf-occ-active > *:not(.bf-occ-panel)' + keep + '{display:none !important}' +
        (STEP_ITEM ? '.bf-occ-active ' + STEP_ITEM + '.active{border-bottom-color:transparent !important;background:transparent !important}' : '') +
        '.bf-occ-tab{display:flex;flex-direction:column;align-items:center;justify-content:flex-end;padding:8px 10px;cursor:pointer;border-bottom:3px solid transparent;box-sizing:border-box;flex:0 0 auto}' +
        '.bf-occ-tab.active{border-bottom-color:#303030;background:rgba(0,0,0,.05)}' +
        '.bf-occ-tab-img{display:block;width:auto;height:75px}' +
        '.bf-occ-tab-text{font-weight:700;text-transform:uppercase}' +
        '.bf-occ-panel{display:none;padding:26px 8px 40px;text-align:center}' +
        '.bf-occ-active > .bf-occ-panel{display:block}' +
        '.bf-occ-title{margin:0 0 6px;font-size:24px;line-height:1.2}' +
        '.bf-occ-sub{margin:0 0 20px;color:inherit;font-style:italic;font-size:15px}' +
        '.bf-occ-options{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;max-width:680px;margin:18px auto 0}' +
        '.bf-occ-option{appearance:none;border:1px solid #d9d4cb;background:#fff;color:#222;border-radius:10px;padding:18px 12px;font:inherit;font-size:16px;line-height:1.2;cursor:pointer;transition:border-color .15s,background .15s,color .15s,box-shadow .15s}' +
        '.bf-occ-option:hover,.bf-occ-option:focus-visible{border-color:' + a2 + ';box-shadow:0 2px 10px rgba(0,0,0,.08);outline:none}' +
        '.bf-occ-option.is-active{background:' + a2 + ';border-color:' + a2 + ';color:#fff}' +
        '.bf-occ-msg{min-height:20px;margin-top:14px;color:#b3261e;font-size:14px}' +
        '.bf-occ-options:has(.bf-occ-option--img){max-width:900px;gap:16px}' +
        '.bf-occ-option--img,.bf-occ-option--img:hover,.bf-occ-option--img:focus-visible{padding:0;border:0;background:transparent;box-shadow:none;border-radius:14px;overflow:hidden;display:flex;align-items:center;justify-content:center}' +
        '.bf-occ-option--img.is-active{background:transparent;outline:3px solid #d3b25a;outline-offset:2px}' +
        '.bf-occ-option--img:hover .bf-occ-img,.bf-occ-option--img:focus-visible .bf-occ-img{transform:translateY(-2px);filter:drop-shadow(0 4px 8px rgba(0,0,0,.12))}' +
        '.bf-occ-option--img:focus-visible{outline:2px solid #7e8b9a;outline-offset:2px}' +
        '.bf-occ-option--img{aspect-ratio:2/1}' +
        '.bf-occ-option--img{min-width:0}.bf-occ-img{display:block;width:100%;max-width:100%;height:100%;object-fit:contain;transition:transform .15s,filter .15s}' +
        '.bf-occ-options .bf-occ-option--all.bf-occ-option--img{grid-column:auto !important}' +
        '@media (max-width:599px){.bf-occ-options{grid-template-columns:repeat(2,minmax(0,1fr))}.bf-occ-option--all{grid-column:1 / -1 !important}.bf-occ-title{font-size:20px}}';
    }
    document.head.appendChild(css);
  }

  // ------------------------------------------------------------------- init
  function watch() {
    // Observe the whole document, not the container: the app may replace the
    // container node itself, which would silently detach a narrower observer.
    var observer = new MutationObserver(function (records) {
      var relevant = records.some(function (r) {
        if (r.type === 'attributes') {
          // a step tab became active (ignore our own tab's changes)
          return String(r.target.className || '').indexOf('bf-') !== 0;
        }
        var nodes = [].slice.call(r.addedNodes).concat([].slice.call(r.removedNodes));
        return nodes.some(function (n) {
          if (n.nodeType !== 1) return false;
          var cls = String(n.className || '');
          if (cls.indexOf('bf-') === 0) return false; // our own bar / notes
          return n.matches && (n.matches(CARD) || n.matches(SECTION) || n.matches(ROOT) || !!n.querySelector(CARD));
        });
      });
      if (relevant) schedule();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    if (STEP_ITEM) {
      // step tabs toggle a class / aria-current; watch those too
      var tabs = document.querySelector(STEP_ITEM);
      var holder = tabs && tabs.parentNode ? tabs.parentNode : null;
      if (holder) observer.observe(holder, { attributes: true, subtree: true, attributeFilter: ['class', 'aria-current'] });
      document.body.addEventListener('click', function () { setTimeout(schedule, 250); }, true);
    }
    if (occ) document.addEventListener('click', onOccClick, true);
    EVENTS.forEach(function (evt) { document.addEventListener(evt, function () { setTimeout(schedule, 50); }); });
  }

  function pickProfile() {
    for (var i = 0; i < PROFILES.length; i++) {
      var p = PROFILES[i];
      var root = p.root && document.querySelector(p.root);
      if (root && p.card && root.querySelector(p.card)) {
        ROOT = p.root; CARD = p.card; SECTION = p.section || p.card;
        HANDLE_ATTR = p.handleAttr || 'data-product-handle';
        INSERT_BEFORE = p.insertBefore || '';
        STEP_ITEM = p.stepItem || '';
        LOAD_MORE = p.loadMore || '';
        STEPS_CONTAINER = p.stepsContainer || '';
        STEP_BACK = p.stepBack || '';
        STEP_NEXT = p.stepNext || '';
        STEP_LABEL = p.stepLabel || '';
        STEP_COUNTER = p.stepCounter || '';
        KEEP_ON_OCC = p.keepOnOccasion || '';
        PROFILE_NAME = p.name || ('profile' + i);
        return true;
      }
    }
    return false;
  }

  function init() {
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      var hasCards = pickProfile();
      var root = hasCards ? document.querySelector(ROOT) : null;
      if (hasCards) {
        clearInterval(timer);
        window.builderFilter.profile = PROFILE_NAME;
        injectStyles();
        readUrl();
        ensureBar(root);
        loadTags(collectHandles(root));
        watch();
        apply();
      } else if (tries > 60) {
        clearInterval(timer);
      }
    }, 500);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
